import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { DispatchService } from '../../services/dispatch.service';

interface DispatchReportRow {
	dispatch_id: number;
	dispnumber: number;
	invoice: string;
	customerno: string;
	branchcode: string;
	datecreated: string;
	dateupdated: string;
	delivery_date: string;
	driveruserid: string;
	dispatch_address: string;
	dispatch_destination?: string;
	status_id: number;
	status_name: string;
	status_completed: number;
	fromlocation: string;
	from_location_name?: string;
	outofbounds: number;
	invoiced: number;
	cleared: number;
	sort: number;
	presort: number;
	fullsort: number;
	esttime: number;
	hometime: number;
	rundistance: string;
	dispatch_additional: number;
	dispatch_type: number;
	passed: number;
	ispoint: number;
	oclater: number;
	dispatch_lat: number | null;
	dispatch_lng: number | null;
	truck_id: number;
	truck_name: string;
	truck_driver: string;
	truck_color: string;
	po_number: string;
	so_phone: string;
	so_email: string;
	salesperson_name: string;
	ship_via_name?: string;
	ship_to_name: string;
	ship_line1: string;
	ship_line2: string;
	ship_city: string;
	ship_state: string;
	ship_postal: string;
	contact_phone: string;
	contact_email: string;
	route_code: string;
	route_description: string;
	line_count: number;
	qty_ordered: number;
	qty_invoiced: number;
	ext_value: number;
	_distance_mi: number;
	_planned_min: number;
	_time_suspicious: boolean;
}

interface FilterTruck { id: number; name: string; driver: string; }
interface FilterStatus { order_status_id: number; name: string; }

interface ReportSummary {
	stops: number;
	completed: number;
	completion_pct: number;
	out_of_bounds: number;
	oc_later: number;
	cleared: number;
	invoiced: number;
	geocoded: number;
	gps_pct: number;
	unique_customers: number;
	unique_trucks: number;
	unique_dispatches: number;
	lines: number;
	qty: number;
	value: number;
	avg_value: number;
	planned_min: number;
	distance_mi: number;
	avg_stop_min: number;
	span_min: number;
}

interface TimelineGroup {
	key: string;
	label: string;
	sublabel: string;
	color: string;
	stops: DispatchReportRow[];
	kpi: {
		stops: number;
		completed: number;
		out_of_bounds: number;
		invoiced: number;
		cleared: number;
		oc_later: number;
		geocoded: number;
		lines: number;
		qty: number;
		value: number;
		planned_min: number;
		home_min: number;
		distance_mi: number;
		avg_stop_min: number;
		avg_value: number;
		first: string | null;
		last: string | null;
		span_min: number;
		completion_pct: number;
		gps_pct: number;
	};
	expanded: boolean;
}

@Component({
	selector: 'app-dispatches-dispatch-report',
	template: `
<div class="main-content dispatch-report-page">
	<div class="container-fluid">
		<div class="card">
			<div class="card-header card-header-{{color}}-5 d-print-none">
				<h4 class="card-title m-0 d-flex align-items-center">
					<i class="material-icons cursor-pointer mr-2" (click)="back()">arrow_back</i>
					<span>Dispatch report</span>
					<span class="ml-auto small text-white-50" *ngIf="rowCount !== null">
						{{ rowCount | number }} stop{{ rowCount === 1 ? '' : 's' }}
						<span *ngIf="totalValue"> · {{ totalValue | currency }}</span>
					</span>
				</h4>
			</div>

			<div class="card-body">
				<form [formGroup]="filterForm" class="row g-2 align-items-end mb-3 d-print-none" (ngSubmit)="load()">
					<div class="col-12 col-md-6 col-lg-5">
						<mat-form-field appearance="outline" class="w-100 range-field">
							<mat-label>Date range</mat-label>
							<mat-date-range-input [rangePicker]="rangePicker">
								<input matStartDate formControlName="dateFrom" placeholder="From" />
								<input matEndDate formControlName="dateTo" placeholder="To" />
							</mat-date-range-input>
							<mat-datepicker-toggle matSuffix [for]="rangePicker"></mat-datepicker-toggle>
							<mat-date-range-picker #rangePicker></mat-date-range-picker>
						</mat-form-field>
					</div>
					<div class="col-6 col-md-3 col-lg-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Truck</mat-label>
							<mat-select formControlName="truckId">
								<mat-option [value]="''">All trucks</mat-option>
								<mat-option *ngFor="let t of trucks" [value]="t.id">
									{{ t.name }}<span *ngIf="t.driver"> — {{ t.driver }}</span>
								</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-6 col-md-3 col-lg-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Status</mat-label>
							<mat-select formControlName="statusId">
								<mat-option [value]="''">All statuses</mat-option>
								<mat-option *ngFor="let s of statuses" [value]="s.order_status_id">{{ s.name }}</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-12 col-lg-3 quick-range-col">
						<div class="quick-range">
							<button mat-stroked-button type="button" (click)="quickRange(1)">Today</button>
							<button mat-stroked-button type="button" (click)="quickRange(7)">7d</button>
							<button mat-stroked-button type="button" (click)="quickRange(30)">30d</button>
							<button mat-stroked-button type="button" (click)="quickRange(90)">90d</button>
						</div>
					</div>

					<div class="col-12 col-md-6">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Search</mat-label>
							<input matInput type="search" [formControl]="searchCtrl" placeholder="invoice, customer, address, driver, route…" />
							<mat-icon matSuffix>search</mat-icon>
						</mat-form-field>
					</div>
					<div class="col-12 col-md-6 toolbar-actions">
						<mat-button-toggle-group [(ngModel)]="viewMode" [ngModelOptions]="{standalone: true}" class="view-toggle" hideSingleSelectionIndicator>
							<mat-button-toggle value="table"><mat-icon>view_list</mat-icon> Table</mat-button-toggle>
							<mat-button-toggle value="timeline-truck"><mat-icon>local_shipping</mat-icon> By truck</mat-button-toggle>
							<mat-button-toggle value="timeline-dispatch"><mat-icon>route</mat-icon> By dispatch</mat-button-toggle>
						</mat-button-toggle-group>
						<button mat-raised-button color="primary" type="submit" [disabled]="loading">
							<mat-icon>refresh</mat-icon> {{ loading ? 'Loading…' : 'Run' }}
						</button>
						<button mat-stroked-button type="button" (click)="exportXlsx()" [disabled]="loading || !filteredData.length">
							<mat-icon>grid_on</mat-icon> XLSX
						</button>
						<button mat-stroked-button type="button" (click)="exportCsv()" [disabled]="loading || !filteredData.length">
							<mat-icon>description</mat-icon> CSV
						</button>
						<button mat-stroked-button type="button" (click)="print()" [disabled]="loading || !filteredData.length">
							<mat-icon>print</mat-icon> Print
						</button>
					</div>
				</form>

				<div *ngIf="error" class="alert alert-warning d-print-none">{{ error }}</div>

				<div class="stats-strip" *ngIf="summary.stops > 0">
					<div class="stat">
						<div class="stat-v">{{ summary.stops | number }}</div>
						<div class="stat-l">stops</div>
					</div>
				<div class="stat">
					<div class="stat-v">{{ summary.completed | number }}<span class="stat-of">/{{ summary.stops }}</span></div>
					<div class="stat-l">invoiced</div>
					<div class="bar"><div class="bar-fill ok" [style.width.%]="summary.completion_pct"></div></div>
				</div>
					<div class="stat">
						<div class="stat-v">{{ summary.unique_dispatches | number }}</div>
						<div class="stat-l">dispatches</div>
					</div>
					<div class="stat">
						<div class="stat-v">{{ summary.unique_trucks | number }}</div>
						<div class="stat-l">trucks</div>
					</div>
					<div class="stat">
						<div class="stat-v">{{ summary.unique_customers | number }}</div>
						<div class="stat-l">customers</div>
					</div>
					<div class="stat">
						<div class="stat-v">{{ summary.lines | number }}</div>
						<div class="stat-l">items</div>
					</div>
					<div class="stat">
						<div class="stat-v">{{ summary.qty | number:'1.0-0' }}</div>
						<div class="stat-l">qty</div>
					</div>
					<div class="stat">
						<div class="stat-v">{{ summary.value | currency:'USD':'symbol':'1.0-0' }}</div>
						<div class="stat-l">value</div>
					</div>
					<div class="stat" *ngIf="summary.avg_value">
						<div class="stat-v">{{ summary.avg_value | currency:'USD':'symbol':'1.0-0' }}</div>
						<div class="stat-l">avg/stop</div>
					</div>
					<div class="stat" *ngIf="summary.planned_min" matTooltip="Total planned minutes (est + additional + home)">
						<div class="stat-v">{{ formatMinutes(summary.planned_min) }}</div>
						<div class="stat-l">planned</div>
					</div>
					<div class="stat" *ngIf="summary.distance_mi" matTooltip="Total run distance">
						<div class="stat-v">{{ formatDistance(summary.distance_mi) }}</div>
						<div class="stat-l">distance</div>
					</div>
				<div class="stat" *ngIf="summary.avg_stop_min" matTooltip="Average est. time per stop (only stops with time data)">
					<div class="stat-v">{{ formatMinutes(summary.avg_stop_min) }}</div>
					<div class="stat-l">avg time</div>
				</div>
				<div class="stat" *ngIf="summary.span_min" matTooltip="From first to last stop creation date">
					<div class="stat-v">{{ formatSpan(summary.span_min) }}</div>
					<div class="stat-l">span</div>
				</div>
					<div class="stat" matTooltip="Stops with GPS coordinates">
						<div class="stat-v">{{ summary.gps_pct }}<span class="stat-of">%</span></div>
						<div class="stat-l">geocoded</div>
						<div class="bar"><div class="bar-fill" [style.width.%]="summary.gps_pct"></div></div>
					</div>
					<div class="stat" *ngIf="summary.oc_later">
						<div class="stat-v">{{ summary.oc_later }}</div>
						<div class="stat-l">OC later</div>
					</div>
					<div class="stat" *ngIf="summary.out_of_bounds">
						<div class="stat-v warn">{{ summary.out_of_bounds }}</div>
						<div class="stat-l">out of bounds</div>
					</div>
				</div>

				<div class="table-wrap mat-elevation-z1" *ngIf="viewMode === 'table'">
					<table mat-table matSort [dataSource]="dataSource" class="report-table">
						<ng-container matColumnDef="invoice">
							<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-invoice">Invoice</th>
							<td mat-cell *matCellDef="let r" class="col-invoice">
						<a *ngIf="r.invoice && !r.invoiced" [routerLink]="['/orders/invoice', r.invoice, r.dispatch_id || '0']" class="link-strong">{{ r.invoice }}</a>
							<span *ngIf="r.invoice && r.invoiced" class="link-strong text-muted">{{ r.invoice }}</span>
							<span *ngIf="!r.invoice">—</span>
								<div class="text-muted small" *ngIf="r.po_number">PO {{ r.po_number }}</div>
							</td>
						</ng-container>

						<ng-container matColumnDef="customer">
							<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-customer">Customer</th>
							<td mat-cell *matCellDef="let r" class="col-customer">
								<a *ngIf="r.customerno" [routerLink]="['/customers/view', r.customerno]" class="link-strong" [matTooltip]="r.ship_to_name">{{ r.ship_to_name || r.customerno }}</a>
								<span *ngIf="!r.customerno">{{ r.ship_to_name }}</span>
								<div class="text-muted small">{{ r.customerno }}<span *ngIf="r.branchcode"> · {{ r.branchcode }}</span></div>
							</td>
						</ng-container>

						<ng-container matColumnDef="address">
							<th mat-header-cell *matHeaderCellDef class="col-address">Address</th>
							<td mat-cell *matCellDef="let r" class="col-address">
								<div class="addr-line1" [matTooltip]="fullAddress(r)">{{ primaryStreet(r) || '—' }}</div>
								<div class="text-muted small">
									<span *ngIf="r.ship_city">{{ r.ship_city }}</span><span *ngIf="r.ship_state">, {{ r.ship_state }}</span>
									<span *ngIf="r.ship_postal"> {{ r.ship_postal }}</span>
									<a *ngIf="mapsUrl(r) as url" [href]="url" target="_blank" rel="noopener" class="ml-1" title="Open in Google Maps">
										<mat-icon class="icon-sm">place</mat-icon>
									</a>
								</div>
							</td>
						</ng-container>

						<ng-container matColumnDef="contact">
							<th mat-header-cell *matHeaderCellDef class="col-contact">Contact</th>
							<td mat-cell *matCellDef="let r" class="col-contact">
								<div *ngIf="r.contact_phone || r.so_phone">
									<a [href]="'tel:' + (r.contact_phone || r.so_phone)">{{ r.contact_phone || r.so_phone }}</a>
								</div>
								<div class="text-muted small" *ngIf="r.contact_email" [matTooltip]="r.contact_email">
									<a [href]="'mailto:' + r.contact_email">{{ r.contact_email }}</a>
								</div>
							</td>
						</ng-container>

						<ng-container matColumnDef="truck">
							<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-truck">Truck / driver</th>
							<td mat-cell *matCellDef="let r" class="col-truck">
								<div>
									<span class="truck-dot" [style.background]="r.truck_color || '#999'"></span>
									<b>{{ r.truck_name }}</b>
								</div>
								<div class="text-muted small">{{ r.truck_driver || r.driveruserid }}</div>
							</td>
						</ng-container>

						<ng-container matColumnDef="route">
							<th mat-header-cell *matHeaderCellDef class="col-route">Route</th>
							<td mat-cell *matCellDef="let r" class="col-route">
								<span *ngIf="r.route_code"><b>{{ r.route_code }}</b></span>
								<span *ngIf="!r.route_code" class="text-muted">—</span>
								<div class="text-muted small" *ngIf="r.route_description">{{ r.route_description }}</div>
							</td>
						</ng-container>

						<ng-container matColumnDef="lines">
							<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-num">Items</th>
							<td mat-cell *matCellDef="let r" class="col-num">
								<span *ngIf="r.line_count">{{ r.line_count | number }}</span>
								<span *ngIf="!r.line_count" class="text-muted">—</span>
							</td>
						</ng-container>

						<ng-container matColumnDef="value">
							<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-money">Value</th>
							<td mat-cell *matCellDef="let r" class="col-money">
								<span *ngIf="r.ext_value">{{ r.ext_value | currency }}</span>
								<span *ngIf="!r.ext_value" class="text-muted">—</span>
							</td>
						</ng-container>

					<ng-container matColumnDef="runtime">
						<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-runtime">Runtime</th>
						<td mat-cell *matCellDef="let r" class="col-runtime">
							<div *ngIf="r._time_suspicious" class="text-warning small" matTooltip="Est. time value looks incorrect — excluded from totals">
								<mat-icon class="icon-sm">warning</mat-icon> bad time data
							</div>
							<div *ngIf="!r._time_suspicious && (r.esttime || r._distance_mi)">
								<span *ngIf="r.esttime">{{ formatMinutes(r.esttime + (r.dispatch_additional || 0)) }}</span>
								<span *ngIf="r._distance_mi" class="text-muted small">
									<span *ngIf="r.esttime"> · </span>{{ formatDistance(r._distance_mi) }}
								</span>
							</div>
							<div *ngIf="!r._time_suspicious && !r.esttime && !r._distance_mi" class="text-muted">—</div>
							<div class="text-muted small chips-inline" *ngIf="r.oclater || r.outofbounds || r.passed">
								<span *ngIf="r.oclater" class="chip warn">OC</span>
								<span *ngIf="r.outofbounds" class="chip warn">OOB</span>
								<span *ngIf="r.passed" class="chip muted">×{{ r.passed }}</span>
							</div>
						</td>
					</ng-container>

						<ng-container matColumnDef="status">
							<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-status">Status</th>
							<td mat-cell *matCellDef="let r" class="col-status">
								<span class="badge-status"
									[class.status-done]="r.status_completed"
									[class.status-warn]="r.outofbounds">{{ r.status_name || 'Unknown' }}</span>
							</td>
						</ng-container>

						<ng-container matColumnDef="datecreated">
							<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-date">Created</th>
							<td mat-cell *matCellDef="let r" class="col-date">{{ r.datecreated | date:'M/d/yy, h:mm a' }}</td>
						</ng-container>

						<ng-container matColumnDef="delivery">
							<th mat-header-cell *matHeaderCellDef mat-sort-header class="col-date">Delivery</th>
							<td mat-cell *matCellDef="let r" class="col-date">
								<span *ngIf="isMeaningfulDate(r.delivery_date)">{{ r.delivery_date | date:'M/d/yy, h:mm a' }}</span>
								<span *ngIf="!isMeaningfulDate(r.delivery_date)" class="text-muted">—</span>
							</td>
						</ng-container>

						<tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
						<tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
					</table>

					<div class="empty" *ngIf="!loading && !filteredData.length">
						<mat-icon>inbox</mat-icon>
						<div>No dispatches found.</div>
						<p class="small text-muted text-center mt-2 mb-0">Widen the date range or clear filters.</p>
					</div>

					<mat-paginator [pageSizeOptions]="[50,100,250,500,1000]" [pageSize]="100" showFirstLastButtons class="d-print-none"></mat-paginator>
				</div>

				<div class="timeline-wrap" *ngIf="viewMode !== 'table'">
					<div class="empty" *ngIf="!loading && !timelineGroups.length">
						<mat-icon>inbox</mat-icon>
						<div>No dispatches in selected window.</div>
					</div>

					<div class="tl-card" *ngFor="let g of timelineGroups; trackBy: trackGroup">
						<div class="tl-head" (click)="g.expanded = !g.expanded">
							<div class="tl-head-left">
								<span class="truck-dot lg" [style.background]="g.color"></span>
								<div class="tl-titles">
									<div class="tl-label">{{ g.label }}</div>
									<div class="tl-sublabel">{{ g.sublabel }}</div>
								</div>
							</div>
							<div class="tl-kpis">
								<div class="kpi"><div class="kpi-v">{{ g.kpi.stops | number }}</div><div class="kpi-l">stops</div></div>
								<div class="kpi"><div class="kpi-v">{{ g.kpi.completed | number }}<span class="kpi-of">/{{ g.kpi.stops }}</span></div><div class="kpi-l">invoiced</div></div>
								<div class="kpi"><div class="kpi-v">{{ g.kpi.lines | number }}</div><div class="kpi-l">items</div></div>
								<div class="kpi"><div class="kpi-v">{{ g.kpi.qty | number:'1.0-0' }}</div><div class="kpi-l">qty</div></div>
								<div class="kpi"><div class="kpi-v">{{ g.kpi.value | currency:'USD':'symbol':'1.0-0' }}</div><div class="kpi-l">value</div></div>
								<div class="kpi" *ngIf="g.kpi.planned_min" matTooltip="Sum of est. + additional + home time"><div class="kpi-v">{{ formatMinutes(g.kpi.planned_min) }}</div><div class="kpi-l">planned</div></div>
								<div class="kpi" *ngIf="g.kpi.distance_mi" matTooltip="Sum of run distance"><div class="kpi-v">{{ formatDistance(g.kpi.distance_mi) }}</div><div class="kpi-l">distance</div></div>
							<div class="kpi" *ngIf="g.kpi.avg_stop_min" matTooltip="Avg est. time per stop with time data"><div class="kpi-v">{{ formatMinutes(g.kpi.avg_stop_min) }}</div><div class="kpi-l">avg/stop</div></div>
							<div class="kpi" *ngIf="g.kpi.span_min" matTooltip="From first to last stop creation"><div class="kpi-v">{{ formatSpan(g.kpi.span_min) }}</div><div class="kpi-l">span</div></div>
								<div class="kpi" *ngIf="g.kpi.oc_later"><div class="kpi-v">{{ g.kpi.oc_later }}</div><div class="kpi-l">OC later</div></div>
								<div class="kpi" matTooltip="Stops with GPS coordinates"><div class="kpi-v">{{ g.kpi.gps_pct }}<span class="kpi-of">%</span></div><div class="kpi-l">geocoded</div></div>
								<div class="kpi" *ngIf="g.kpi.out_of_bounds"><div class="kpi-v warn">{{ g.kpi.out_of_bounds }}</div><div class="kpi-l">OOB</div></div>
							</div>
						<div class="tl-progress">
							<div class="tl-progress-track">
								<div class="tl-progress-bar" [style.width.%]="g.kpi.completion_pct"></div>
							</div>
							<span class="tl-progress-pct">{{ g.kpi.completion_pct | number:'1.0-0' }}%</span>
							<span class="tl-progress-label">complete</span>
						</div>
						<mat-icon class="tl-chevron">{{ g.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
						</div>
					<div class="tl-stops" *ngIf="g.expanded">
						<div class="tl-stop" *ngFor="let s of g.stops; let i = index">
							<div class="tl-seq">
								<div class="tl-seq-num">{{ i + 1 }}</div>
								<div class="tl-seq-line" *ngIf="i < g.stops.length - 1"></div>
							</div>
							<div class="tl-stop-body">
								<div class="tl-stop-row tl-stop-header">
									<div class="tl-stop-header-left">
										<a *ngIf="s.invoice && !s.invoiced" [routerLink]="['/orders/invoice', s.invoice, s.dispatch_id || '0']" class="link-strong">{{ s.invoice }}</a>
										<span *ngIf="s.invoice && s.invoiced" class="link-strong text-muted">{{ s.invoice }}</span>
										<span *ngIf="!s.invoice" class="text-muted small">no invoice</span>
										<span class="badge-status sm"
											[class.status-done]="s.status_completed"
											[class.status-warn]="s.outofbounds">{{ s.status_name || 'Unknown' }}</span>
									</div>
									<div class="tl-stop-header-right">
										<span class="tl-meta" *ngIf="viewMode !== 'timeline-dispatch'">Disp #{{ s.dispnumber }} ·</span>
										<span class="tl-meta">seq {{ s.fullsort || s.sort || '?' }}</span>
										<span class="tl-meta" *ngIf="s.ext_value"> · {{ s.ext_value | currency }}</span>
										<span class="tl-meta" *ngIf="s.line_count"> · {{ s.line_count }} item{{ s.line_count === 1 ? '' : 's' }}</span>
									</div>
								</div>
								<div class="tl-stop-row">
									<a *ngIf="s.customerno" [routerLink]="['/customers/view', s.customerno]" class="link-strong">{{ s.ship_to_name || s.customerno }}</a>
									<span *ngIf="!s.customerno" class="link-strong">{{ s.ship_to_name }}</span>
									<span class="tl-cust-num text-muted" *ngIf="s.customerno">{{ s.customerno }}<span *ngIf="s.branchcode">-{{ s.branchcode }}</span></span>
								</div>
								<div class="tl-stop-row tl-stop-addr text-muted small">
									<span>{{ primaryStreet(s) }}<span *ngIf="s.ship_city">, {{ s.ship_city }}</span><span *ngIf="s.ship_state">, {{ s.ship_state }}</span><span *ngIf="s.ship_postal"> {{ s.ship_postal }}</span></span>
									<a *ngIf="mapsUrl(s) as url" [href]="url" target="_blank" rel="noopener" class="tl-icon-link" title="Open in Google Maps">
										<mat-icon class="icon-sm">place</mat-icon>
									</a>
									<a *ngIf="s.contact_phone || s.so_phone" [href]="'tel:' + (s.contact_phone || s.so_phone)" class="tl-icon-link tl-phone-link">
										<mat-icon class="icon-sm">phone</mat-icon>{{ s.contact_phone || s.so_phone }}
									</a>
								</div>
								<div class="tl-stop-row tl-stop-dates text-muted small">
									<span class="tl-date-item"><span class="tl-date-lbl">cr</span> {{ s.datecreated | date:'M/d/yy h:mm a' }}</span>
									<span class="tl-date-item" *ngIf="isMeaningfulDate(s.delivery_date)"><span class="tl-date-lbl">del</span> {{ s.delivery_date | date:'M/d/yy h:mm a' }}</span>
									<span class="tl-date-item" *ngIf="s.route_code"><span class="tl-date-lbl">rt</span> {{ s.route_code }}</span>
									<span class="tl-date-item" *ngIf="viewMode === 'timeline-dispatch' && s.truck_name">{{ s.truck_name }}<span *ngIf="s.truck_driver"> · {{ s.truck_driver }}</span></span>
								</div>
								<div class="tl-stop-row chips">
									<span class="chip warn" *ngIf="s._time_suspicious" matTooltip="Est. time looks like seconds stored as minutes — excluded from totals">
										<mat-icon class="icon-sm">warning</mat-icon> bad time
									</span>
									<span class="chip" *ngIf="s.esttime && !s._time_suspicious" matTooltip="Estimated stop time">
										<mat-icon class="icon-sm">schedule</mat-icon> est {{ formatMinutes(s.esttime) }}
									</span>
									<span class="chip" *ngIf="s.esttime && s._time_suspicious" matTooltip="Suspicious value: {{ s.esttime }} min — possible data error">
										<mat-icon class="icon-sm">schedule</mat-icon> <s>{{ formatMinutes(s.esttime) }}</s>
									</span>
									<span class="chip" *ngIf="s.dispatch_additional && !s._time_suspicious" matTooltip="Additional buffer time">+{{ formatMinutes(s.dispatch_additional) }}</span>
									<span class="chip" *ngIf="s._distance_mi" matTooltip="Run distance">
										<mat-icon class="icon-sm">straighten</mat-icon> {{ formatDistance(s._distance_mi) }}
									</span>
									<span class="chip" *ngIf="s.hometime" matTooltip="Home leg time">
										<mat-icon class="icon-sm">home</mat-icon> {{ formatMinutes(s.hometime) }}
									</span>
									<span class="chip" *ngIf="s.passed" matTooltip="Times this stop was passed">
										<mat-icon class="icon-sm">repeat</mat-icon> ×{{ s.passed }}
									</span>
									<span class="chip warn" *ngIf="s.outofbounds" matTooltip="Out of bounds">OOB</span>
									<span class="chip warn" *ngIf="s.oclater" matTooltip="On-call later">OC later</span>
									<span class="chip ok" *ngIf="s.cleared" matTooltip="Cleared">cleared</span>
									<span class="chip ok" *ngIf="s.invoiced" matTooltip="Invoiced">invoiced</span>
									<span class="chip muted" *ngIf="!hasGps(s)" matTooltip="No GPS coordinates">no GPS</span>
									<span class="chip muted" *ngIf="s.ispoint" matTooltip="Single point (not a route)">point</span>
								</div>
							</div>
						</div>
					</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
`,
	styles: [
		`
			:host { display: block; }
			.cursor-pointer { cursor: pointer; }
			.icon-sm { font-size: 16px; height: 16px; width: 16px; vertical-align: middle; opacity: .6; }
			.icon-sm:hover { opacity: 1; }

			.range-field ::ng-deep .mat-date-range-input-container { align-items: center; }
			.range-field ::ng-deep .mat-date-range-input-separator {
				color: rgba(0,0,0,.54); margin: 0 .5rem;
			}
			.range-field ::ng-deep input.mat-date-range-input-inner {
				font-variant-numeric: tabular-nums;
			}

			.quick-range-col { display: flex; align-items: flex-end; padding-bottom: 1.5rem; }
			.quick-range {
				display: inline-flex; gap: 0; border-radius: 6px; overflow: hidden;
				width: 100%;
			}
			.quick-range button {
				flex: 1; min-width: 0; border-radius: 0 !important;
				border-right-width: 0 !important;
			}
			.quick-range button:first-child { border-top-left-radius: 6px !important; border-bottom-left-radius: 6px !important; }
			.quick-range button:last-child { border-top-right-radius: 6px !important; border-bottom-right-radius: 6px !important; border-right-width: 1px !important; }

			.toolbar-actions {
				display: flex; flex-wrap: wrap; gap: .35rem; justify-content: flex-end; align-items: center;
				padding-bottom: 1.25rem;
			}
			.toolbar-actions button mat-icon { vertical-align: middle; font-size: 18px; height: 18px; width: 18px; }

			.table-wrap { background: #fff; border-radius: 8px; overflow: auto; }
			.report-table { width: 100%; min-width: 1200px; table-layout: fixed; }
			.report-table th, .report-table td { padding: 8px 10px !important; vertical-align: top; overflow: hidden; }
			.report-table .mat-header-cell, .report-table .mat-mdc-header-cell { background: #f8fafc; font-weight: 600; color: #374151; }
			.report-table .mat-row:hover, .report-table .mat-mdc-row:hover { background: #f9fafb; }
			.report-table .col-num { width: 70px; text-align: right; white-space: nowrap; }
			.report-table .col-money { width: 100px; text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
			.report-table .col-status { width: 110px; }
			.report-table .col-date { width: 130px; white-space: nowrap; font-variant-numeric: tabular-nums; }
			.report-table .col-truck { width: 140px; }
			.report-table .col-route { width: 80px; }
			.report-table .col-invoice { width: 100px; }
			.report-table .col-runtime { width: 130px; white-space: nowrap; }
			.report-table .col-customer { width: 200px; }
			.report-table .col-address { width: 240px; }
			.report-table .col-contact { width: 170px; }
			.report-table .cell-wrap, .report-table .col-customer, .report-table .col-address, .report-table .col-contact {
				white-space: normal; overflow-wrap: break-word; word-break: normal;
			}
			.report-table .col-address .addr-line1 {
				display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
				overflow: hidden; text-overflow: ellipsis; line-height: 1.3;
			}
			.chips-inline { display: inline-flex; gap: 3px; margin-top: 2px; }
			.chips-inline .chip { font-size: .65rem; padding: 0 6px; }
			.text-right { text-align: right; }

			.stats-strip {
				display: flex;
				flex-direction: row;
				flex-wrap: nowrap;
				overflow-x: auto;
				gap: 0;
				background: #fff; border-radius: 10px;
				box-shadow: 0 1px 3px rgba(0,0,0,.06);
				margin-bottom: .75rem;
				scrollbar-width: thin;
			}
			.stats-strip::-webkit-scrollbar { height: 4px; }
			.stats-strip::-webkit-scrollbar-track { background: #f1f5f9; }
			.stats-strip::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
			.stat {
				flex: 0 0 auto;
				min-width: 90px;
				padding: .65rem .85rem;
				border-right: 1px solid #f1f5f9;
				display: flex; flex-direction: column; gap: 3px;
			}
			.stat:last-child { border-right: none; }
			.stat:first-child { border-left: 3px solid #6366f1; padding-left: .65rem; }
			.stat-v {
				font-weight: 700; font-size: 1.05rem; color: #111827;
				font-variant-numeric: tabular-nums; line-height: 1.2; white-space: nowrap;
			}
			.stat-v.warn { color: #b91c1c; }
			.stat-of { color: #9ca3af; font-weight: 500; font-size: .8rem; margin-left: 2px; }
			.stat-l { color: #6b7280; font-size: .65rem; text-transform: uppercase; letter-spacing: .06em; white-space: nowrap; }
			.bar {
				height: 3px; background: #f1f5f9; border-radius: 999px; overflow: hidden; margin-top: 4px;
			}
			.bar-fill { height: 100%; background: #6366f1; transition: width .3s; }
			.bar-fill.ok { background: linear-gradient(90deg, #16a34a, #22c55e); }

			.link-strong { font-weight: 600; }
			.text-warning { color: #b45309 !important; display: flex; align-items: center; gap: 3px; }
			.truck-dot {
				display: inline-block; width: 10px; height: 10px; border-radius: 50%;
				margin-right: 6px; vertical-align: middle;
			}

			.badge-status {
				display: inline-block; padding: 2px 10px; border-radius: 999px;
				background: #e0f2fe; color: #075985; font-size: .78rem; font-weight: 600;
			}
			.badge-status.status-done { background: #dcfce7; color: #166534; }
			.badge-status.status-warn { background: #fee2e2; color: #991b1b; }

			.empty {
				display: flex; flex-direction: column; align-items: center; justify-content: center;
				color: #6b7280; padding: 2.5rem 1rem;
			}
			.empty mat-icon { font-size: 48px; height: 48px; width: 48px; opacity: .35; margin-bottom: .5rem; }

			.view-toggle { margin-right: auto; }
			.view-toggle ::ng-deep .mat-button-toggle-label-content { line-height: 36px !important; padding: 0 12px !important; display: inline-flex; align-items: center; gap: 4px; }
			.view-toggle ::ng-deep .mat-button-toggle-label-content mat-icon { font-size: 18px; height: 18px; width: 18px; }

			.truck-dot.lg { width: 14px; height: 14px; }
			.badge-status.sm { font-size: .68rem; padding: 1px 8px; }

			.timeline-wrap { background: transparent; }
			.tl-card {
				background: #fff; border-radius: 10px; box-shadow: 0 1px 3px rgba(0,0,0,.08);
				margin-bottom: .75rem; overflow: hidden; border: 1px solid #f1f5f9;
			}
			.tl-head {
				display: flex; flex-direction: row; align-items: center;
				gap: 0; padding: 0; cursor: pointer;
				border-bottom: 1px solid transparent; transition: background .12s, border-color .12s;
				overflow: hidden;
			}
			.tl-head:hover { background: #f9fafb; }
			.tl-head-left {
				display: flex; align-items: center; gap: .6rem;
				min-width: 200px; max-width: 220px; flex: 0 0 220px;
				padding: .75rem .85rem; border-right: 1px solid #f1f5f9;
			}
			.tl-titles { min-width: 0; }
			.tl-label { font-weight: 700; font-size: .95rem; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
			.tl-sublabel { color: #6b7280; font-size: .75rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

			.tl-kpis {
				display: flex; flex: 1 1 auto; overflow-x: auto; align-items: stretch;
				flex-wrap: nowrap; gap: 0;
				scrollbar-width: none;
			}
			.tl-kpis::-webkit-scrollbar { display: none; }
			.kpi {
				flex: 0 0 auto; min-width: 70px;
				text-align: center; line-height: 1.1;
				padding: .65rem .7rem;
				border-right: 1px solid #f1f5f9;
				display: flex; flex-direction: column; justify-content: center; gap: 2px;
			}
			.kpi:last-child { border-right: none; }
			.kpi-v { font-weight: 700; font-size: .92rem; color: #111827; font-variant-numeric: tabular-nums; white-space: nowrap; }
			.kpi-v.warn { color: #b91c1c; }
			.kpi-of { color: #9ca3af; font-weight: 500; font-size: .78rem; margin-left: 1px; }
			.kpi-l { color: #6b7280; font-size: .62rem; text-transform: uppercase; letter-spacing: .05em; white-space: nowrap; }

			.tl-progress {
				flex: 0 0 auto; width: 110px;
				display: flex; flex-direction: column; align-items: center; justify-content: center;
				padding: .65rem .85rem; gap: 4px; border-left: 1px solid #f1f5f9;
			}
			.tl-progress-track {
				width: 100%; height: 6px; background: #f1f5f9; border-radius: 999px; overflow: hidden;
			}
			.tl-progress-bar {
				height: 100%; background: linear-gradient(90deg, #16a34a, #22c55e);
				transition: width .3s;
			}
			.tl-progress-pct {
				font-size: .75rem; color: #374151; font-variant-numeric: tabular-nums; font-weight: 700;
			}
			.tl-progress-label {
				font-size: .6rem; color: #6b7280; text-transform: uppercase; letter-spacing: .05em;
			}
			.tl-chevron { color: #9ca3af; flex: 0 0 36px; text-align: center; }

			.tl-stops { padding: .35rem 1rem 1rem 1rem; border-top: 1px solid #f1f5f9; background: #fbfdff; }
			.tl-stop { display: grid; grid-template-columns: 30px 1fr; gap: .6rem; padding: .55rem 0; border-bottom: 1px solid #f8fafc; }
			.tl-stop:last-child { border-bottom: none; }
			.tl-seq { display: flex; flex-direction: column; align-items: center; }
			.tl-seq-num {
				width: 24px; height: 24px; border-radius: 50%;
				background: #e0e7ff; color: #3730a3; font-weight: 700; font-size: .72rem;
				display: flex; align-items: center; justify-content: center; flex-shrink: 0;
			}
			.tl-seq-line { flex: 1; width: 2px; background: #e5e7eb; margin-top: 4px; min-height: 12px; }
			.tl-stop-body { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
			.tl-stop-row { display: flex; gap: .5rem; align-items: center; flex-wrap: wrap; min-width: 0; }
			.tl-meta { color: #9ca3af; font-size: .78rem; white-space: nowrap; }
			.tl-meta a { color: inherit; }

			.tl-stop-header { justify-content: space-between; flex-wrap: nowrap; gap: .5rem; }
			.tl-stop-header-left { display: flex; align-items: center; gap: .45rem; flex-shrink: 1; min-width: 0; }
			.tl-stop-header-right { display: flex; align-items: center; gap: 0; flex-shrink: 0; white-space: nowrap; }

			.tl-cust-num { font-size: .75rem; }

			.tl-stop-addr { gap: .4rem; flex-wrap: wrap; }
			.tl-icon-link { display: inline-flex; align-items: center; gap: 2px; color: #6b7280; text-decoration: none; }
			.tl-icon-link:hover { color: #374151; }
			.tl-phone-link { gap: 3px; }

			.tl-stop-dates { gap: .6rem; flex-wrap: wrap; }
			.tl-date-item { display: inline-flex; align-items: baseline; gap: 3px; white-space: nowrap; }
			.tl-date-lbl { font-size: .6rem; text-transform: uppercase; letter-spacing: .05em; color: #9ca3af; font-weight: 700; }

			.tl-stop-row.chips { gap: .35rem; margin-top: 2px; }
			.chip {
				display: inline-flex; align-items: center; gap: 3px;
				padding: 1px 8px; border-radius: 999px;
				background: #eef2ff; color: #3730a3; font-size: .72rem; font-weight: 600;
				line-height: 1.25;
			}
			.chip.warn { background: #fee2e2; color: #991b1b; }
			.chip.ok   { background: #dcfce7; color: #166534; }
			.chip.muted { background: #f3f4f6; color: #6b7280; }
			.chip mat-icon { font-size: 13px !important; height: 13px !important; width: 13px !important; opacity: .9; }

			@media (max-width: 768px) {
				.tl-head-left { flex: 0 0 160px; min-width: 160px; max-width: 160px; }
				.tl-progress { width: 90px; }
				.stats-strip .stat { min-width: 80px; padding: .5rem .65rem; }
			}

			@media print {
				.card { box-shadow: none !important; }
				.report-table { font-size: 11px; }
				.tl-head { cursor: default; }
				.tl-stops { display: block !important; }
				.view-toggle, .toolbar-actions, .quick-range-col, .d-print-none { display: none !important; }
			}
		`
	]
})
export class DispatchesDispatchReportComponent implements OnInit, AfterViewInit {
	color = 'blue';
	loading = false;
	error: string | null = null;

	trucks: FilterTruck[] = [];
	statuses: FilterStatus[] = [];

	allRows: DispatchReportRow[] = [];
	filteredData: DispatchReportRow[] = [];
	timelineGroups: TimelineGroup[] = [];
	rowCount: number | null = null;
	totalValue = 0;
	summary: ReportSummary = this.emptySummary();
	private _viewMode: 'table' | 'timeline-truck' | 'timeline-dispatch' = 'table';
	get viewMode(): 'table' | 'timeline-truck' | 'timeline-dispatch' { return this._viewMode; }
	set viewMode(v: 'table' | 'timeline-truck' | 'timeline-dispatch') {
		if (v === this._viewMode) return;
		this._viewMode = v;
		this.buildTimelines();
	}

	filterForm = new FormGroup({
		dateFrom: new FormControl<Date | null>(null),
		dateTo: new FormControl<Date | null>(null),
		truckId: new FormControl<number | ''>(''),
		statusId: new FormControl<number | ''>('')
	});
	searchCtrl = new FormControl<string>('');

	displayedColumns: string[] = [
		'invoice', 'customer', 'address', 'contact',
		'truck', 'route', 'lines', 'value', 'runtime', 'status', 'datecreated', 'delivery'
	];

	dataSource = new MatTableDataSource<DispatchReportRow>([]);

	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	constructor(
		private location: Location,
		private dispatchService: DispatchService,
		private globalSearchService: GlobalSearchService,
		private cdr: ChangeDetectorRef
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.quickRange(7);
		this.searchCtrl.valueChanges.subscribe(() => this.applySearch());
		this.dispatchService.getTrucks().subscribe({
			next: (rows: any[]) => {
				this.trucks = (rows || []).map((t: any) => ({
					id: t.id,
					name: t.name || '',
					driver: t.driver || ''
				}));
			},
			error: () => { this.trucks = []; }
		});
		this.load();
	}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.dataSource.sortingDataAccessor = (row: DispatchReportRow, column: string) => {
			switch (column) {
				case 'invoice': return row.invoice;
				case 'customer': return row.ship_to_name || row.customerno;
				case 'truck': return `${row.truck_name} ${row.truck_driver}`;
				case 'lines': return row.line_count;
				case 'value': return row.ext_value;
				case 'runtime': return (row.esttime || 0) + (row.dispatch_additional || 0);
				case 'status': return row.status_name || '';
				case 'datecreated': return row.datecreated;
				case 'delivery': return row.delivery_date;
				default: return '';
			}
		};
	}

	back(): void { this.location.back(); }

	private pad(n: number): string { return String(n).padStart(2, '0'); }
	private toApiDatetime(d: Date | null, endOfDay: boolean): string {
		if (!d) return '';
		const datePart = `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
		return endOfDay ? `${datePart} 23:59:59` : `${datePart} 00:00:00`;
	}

	quickRange(days: number): void {
		const start = new Date();
		const end = new Date();
		start.setHours(0, 0, 0, 0);
		end.setHours(0, 0, 0, 0);
		start.setDate(start.getDate() - Math.max(0, days - 1));
		this.filterForm.patchValue({ dateFrom: start, dateTo: end });
	}

	load(): void {
		const v = this.filterForm.value;
		const fromD = v.dateFrom ?? null;
		const toD = v.dateTo ?? null;
		if (!fromD || !toD) {
			this.error = 'Pick a From and To date.';
			return;
		}
		const startMid = new Date(fromD); startMid.setHours(0, 0, 0, 0);
		const endMid = new Date(toD); endMid.setHours(0, 0, 0, 0);
		if (endMid < startMid) {
			this.error = 'To date must be on or after From date.';
			return;
		}
		this.loading = true;
		this.error = null;

		const payload: any = {
			date_from: this.toApiDatetime(fromD, false),
			date_to: this.toApiDatetime(toD, true)
		};
		if (v.truckId !== '' && v.truckId != null) payload.truck_id = Number(v.truckId);
		if (v.statusId !== '' && v.statusId != null) payload.status_id = Number(v.statusId);

		this.dispatchService.getDispatchReport(payload).subscribe({
			next: (res: any) => {
				this.allRows = (res.rows || []).map((r: any) => this.normalize(r));
				this.statuses = (res.statuses || []).map((s: any) => ({
					order_status_id: s.order_status_id,
					name: s.name
				}));
				this.applySearch();
				this.loading = false;
			},
			error: (err: any) => {
				this.loading = false;
				this.error = err?.error?.error || err?.message || 'Failed to load report.';
			}
		});
	}

	private stripHtml(s: any): string {
		if (s === null || s === undefined) return '';
		return String(s)
			.replace(/<\s*br\s*\/?\s*>/gi, ' ')
			.replace(/<[^>]+>/g, ' ')
			.replace(/&nbsp;/gi, ' ')
			.replace(/&amp;/gi, '&')
			.replace(/&lt;/gi, '<')
			.replace(/&gt;/gi, '>')
			.replace(/\s{2,}/g, ' ')
			.trim();
	}

	private parseDistance(s: any): number {
		if (s === null || s === undefined) return 0;
		const m = String(s).match(/-?\d+(?:\.\d+)?/);
		return m ? parseFloat(m[0]) : 0;
	}

	formatMinutes(min: number): string {
		const m = Math.max(0, Math.round(min || 0));
		if (m < 1) return '0m';
		if (m < 60) return m + 'm';
		const h = Math.floor(m / 60);
		const r = m % 60;
		return r === 0 ? h + 'h' : h + 'h ' + r + 'm';
	}

	formatSpan(min: number): string {
		const m = Math.max(0, Math.round(min || 0));
		if (m < 1) return '0m';
		if (m < 60) return m + 'm';
		const totalHours = Math.floor(m / 60);
		if (totalHours < 48) {
			const r = m % 60;
			return r === 0 ? totalHours + 'h' : totalHours + 'h ' + r + 'm';
		}
		const days = Math.floor(totalHours / 24);
		const remH = totalHours % 24;
		return remH === 0 ? days + 'd' : days + 'd ' + remH + 'h';
	}

	formatDistance(mi: number): string {
		const v = Number(mi || 0);
		if (v <= 0) return '0 mi';
		return (v < 10 ? v.toFixed(1) : Math.round(v).toString()) + ' mi';
	}

	private normalize(r: any): DispatchReportRow {
		const esttime = Number(r.esttime || 0);
		const additional = Number(r.dispatch_additional || r.additional || 0);
		const hometime = Number(r.hometime || 0);
		const distance = this.parseDistance(r.rundistance);
		// Per-stop values > 4 hours are almost certainly data errors (seconds stored as minutes).
		// All three time fields are checked — hometime included.
		const timeSuspicious = esttime > 240 || additional > 240 || hometime > 240;
		const safeEst = timeSuspicious ? 0 : esttime;
		const safeAdd = timeSuspicious ? 0 : additional;
		const safeHome = timeSuspicious ? 0 : hometime;
		const planned = safeEst + safeAdd + safeHome;
		const lat = (r.dispatch_lat !== undefined && r.dispatch_lat !== null && r.dispatch_lat !== '') ? Number(r.dispatch_lat) : null;
		const lng = (r.dispatch_lng !== undefined && r.dispatch_lng !== null && r.dispatch_lng !== '') ? Number(r.dispatch_lng) : null;
		return {
			...r,
			dispatch_id: Number(r.dispatch_id || 0),
			dispnumber: Number(r.dispnumber || 0),
			line_count: Number(r.line_count || 0),
			qty_ordered: Number(r.qty_ordered || 0),
			qty_invoiced: Number(r.qty_invoiced || 0),
			ext_value: Number(r.ext_value || 0),
			invoiced: Number(r.invoiced || 0),
			cleared: Number(r.cleared || 0),
			outofbounds: Number(r.outofbounds || 0),
			status_completed: Number(r.status_completed || 0),
			sort: Number(r.sort || 0),
			presort: Number(r.presort || 0),
			fullsort: Number(r.fullsort || 0),
			esttime: esttime,
			hometime: hometime,
			rundistance: r.rundistance || '',
			dispatch_additional: additional,
			dispatch_type: Number(r.dispatch_type || r.type || 0),
			passed: Number(r.passed || 0),
			ispoint: Number(r.ispoint || 0),
			oclater: Number(r.oclater || 0),
			dispatch_lat: lat,
			dispatch_lng: lng,
			truck_id: Number(r.truck_id || 0),
			ship_to_name: this.stripHtml(r.ship_to_name),
			ship_line1: this.stripHtml(r.ship_line1),
			ship_line2: this.stripHtml(r.ship_line2),
			ship_city: this.stripHtml(r.ship_city),
			ship_state: this.stripHtml(r.ship_state),
			ship_postal: this.stripHtml(r.ship_postal),
			dispatch_address: this.stripHtml(r.dispatch_address),
			dispatch_destination: this.stripHtml(r.dispatch_destination),
		_distance_mi: distance,
		_planned_min: planned,
		_time_suspicious: timeSuspicious
	};
	}

	hasGps(s: DispatchReportRow): boolean {
		return s.dispatch_lat !== null && s.dispatch_lng !== null
			&& !(s.dispatch_lat === 0 && s.dispatch_lng === 0);
	}

	private compareNumbers(a: number, b: number): number { return (a || 0) - (b || 0); }
	private compareStrings(a: string, b: string): number { return (a || '').localeCompare(b || ''); }

	private sortByDispatchAndSequence(rows: DispatchReportRow[]): DispatchReportRow[] {
		return rows.slice().sort((a, b) => {
			const d = this.compareNumbers(b.dispnumber, a.dispnumber);
			if (d !== 0) return d;
			const fs = this.compareNumbers(a.fullsort, b.fullsort);
			if (fs !== 0) return fs;
			const s = this.compareNumbers(a.sort, b.sort);
			if (s !== 0) return s;
			return this.compareNumbers(b.dispatch_id, a.dispatch_id);
		});
	}

	private sortStopsForTruck(rows: DispatchReportRow[]): DispatchReportRow[] {
		return rows.slice().sort((a, b) => {
			const d = this.compareNumbers(b.dispnumber, a.dispnumber);
			if (d !== 0) return d;
			const fs = this.compareNumbers(a.fullsort, b.fullsort);
			if (fs !== 0) return fs;
			return this.compareNumbers(a.sort, b.sort);
		});
	}

	applySearch(): void {
		const term = (this.searchCtrl.value || '').toLowerCase().trim();
		let rows: DispatchReportRow[];
		if (!term) {
			rows = this.allRows.slice();
		} else {
			rows = this.allRows.filter((r) => {
				const hay = [
					r.invoice, r.po_number, r.customerno, r.branchcode,
					r.ship_to_name, r.ship_line1, r.ship_city, r.ship_state, r.ship_postal,
					r.dispatch_address, r.contact_phone, r.contact_email,
					r.truck_name, r.truck_driver, r.driveruserid,
					r.route_code, r.route_description, r.status_name, r.salesperson_name
				].join(' ').toLowerCase();
				return hay.indexOf(term) !== -1;
			});
		}
		this.filteredData = this.sortByDispatchAndSequence(rows);
		this.dataSource.data = this.filteredData;
		this.rowCount = this.filteredData.length;
		this.totalValue = this.filteredData.reduce((s, r) => s + (r.ext_value || 0), 0);
		this.summary = this.computeSummary(this.filteredData);
		this.buildTimelines();
		this.cdr.markForCheck();
	}

	private emptySummary(): ReportSummary {
		return {
			stops: 0, completed: 0, completion_pct: 0,
			out_of_bounds: 0, oc_later: 0, cleared: 0, invoiced: 0,
			geocoded: 0, gps_pct: 0,
			unique_customers: 0, unique_trucks: 0, unique_dispatches: 0,
			lines: 0, qty: 0, value: 0, avg_value: 0,
			planned_min: 0, distance_mi: 0, avg_stop_min: 0, span_min: 0
		};
	}

	private computeSummary(rows: DispatchReportRow[]): ReportSummary {
		const s = this.emptySummary();
		if (!rows.length) return s;
		const customers = new Set<string>();
		const trucks = new Set<number>();
		const dispatches = new Set<number>();
		const distanceSeen = new Set<number>();
		let stopMinSum = 0;
		let stopMinCount = 0;
		let first: number | null = null;
		let last: number | null = null;
		for (const r of rows) {
			s.stops += 1;
			s.completed += r.invoiced ? 1 : 0;
			s.out_of_bounds += r.outofbounds ? 1 : 0;
			s.oc_later += r.oclater ? 1 : 0;
			s.cleared += r.cleared ? 1 : 0;
			s.invoiced += r.invoiced ? 1 : 0;
			s.geocoded += this.hasGps(r) ? 1 : 0;
			s.lines += r.line_count || 0;
			s.qty += r.qty_ordered || 0;
			s.value += r.ext_value || 0;
			s.planned_min += r._planned_min || 0;
			// rundistance is the full route distance shared by all stops in the dispatch — count once per dispatch
			if (r.dispnumber && !distanceSeen.has(r.dispnumber)) {
				distanceSeen.add(r.dispnumber);
				s.distance_mi += r._distance_mi || 0;
			}
			if (!r._time_suspicious) {
				const stopTime = (r.esttime || 0) + (r.dispatch_additional || 0);
				if (stopTime > 0) {
					stopMinSum += stopTime;
					stopMinCount++;
				}
			}
			if (r.customerno) customers.add(r.customerno + '|' + (r.branchcode || ''));
			if (r.truck_id) trucks.add(r.truck_id);
			if (r.dispnumber) dispatches.add(r.dispnumber);
			if (r.datecreated) {
				const t = new Date(r.datecreated).getTime();
				if (!Number.isNaN(t)) {
					if (first === null || t < first) first = t;
					if (last === null || t > last) last = t;
				}
			}
		}
		s.unique_customers = customers.size;
		s.unique_trucks = trucks.size;
		s.unique_dispatches = dispatches.size;
		s.completion_pct = Math.round((s.completed / s.stops) * 100);
		s.gps_pct = Math.round((s.geocoded / s.stops) * 100);
		s.avg_stop_min = stopMinCount > 0 ? stopMinSum / stopMinCount : 0;
		s.avg_value = s.value / s.stops;
		if (first !== null && last !== null) {
			s.span_min = Math.max(0, Math.round((last - first) / 60000));
		}
		return s;
	}

	private buildTimelines(): void {
		const mode = this.viewMode === 'timeline-dispatch' ? 'dispatch' : 'truck';
		const groups = new Map<string, TimelineGroup>();
		for (const r of this.filteredData) {
			let key: string; let label: string; let sublabel: string; let color: string;
			if (mode === 'dispatch') {
				key = `D-${r.dispnumber}`;
				label = `Dispatch #${r.dispnumber}`;
				const driver = r.truck_driver || r.driveruserid || '';
				sublabel = `${r.truck_name || 'Unassigned'}${driver ? ' · ' + driver : ''}` +
					`${r.route_code ? ' · Route ' + r.route_code : ''}`;
				color = r.truck_color || '#6366f1';
			} else {
				key = `T-${r.truck_id || 0}`;
				label = r.truck_name || 'Unassigned';
				sublabel = (r.truck_driver || r.driveruserid || 'No driver assigned');
				color = r.truck_color || '#6b7280';
			}
			let g = groups.get(key);
			if (!g) {
				g = {
					key, label, sublabel, color,
					stops: [],
					kpi: {
						stops: 0, completed: 0, out_of_bounds: 0, invoiced: 0, cleared: 0,
						oc_later: 0, geocoded: 0,
						lines: 0, qty: 0, value: 0,
						planned_min: 0, home_min: 0, distance_mi: 0,
						avg_stop_min: 0, avg_value: 0,
						first: null, last: null, span_min: 0,
						completion_pct: 0, gps_pct: 0
					},
					expanded: false
				};
				groups.set(key, g);
			}
			g.stops.push(r);
		}

		const out: TimelineGroup[] = [];
		groups.forEach((g) => {
			g.stops = this.sortStopsForTruck(g.stops);
			let first: string | null = null;
			let last: string | null = null;
			let stopMinSum = 0;
			let stopMinCount = 0;
			const distanceSeen = new Set<number>();
			for (const s of g.stops) {
				g.kpi.stops += 1;
				g.kpi.completed += s.invoiced ? 1 : 0;
				g.kpi.out_of_bounds += s.outofbounds ? 1 : 0;
				g.kpi.invoiced += s.invoiced ? 1 : 0;
				g.kpi.cleared += s.cleared ? 1 : 0;
				g.kpi.oc_later += s.oclater ? 1 : 0;
				g.kpi.geocoded += this.hasGps(s) ? 1 : 0;
				g.kpi.lines += s.line_count || 0;
				g.kpi.qty += s.qty_ordered || 0;
				g.kpi.value += s.ext_value || 0;
				g.kpi.planned_min += s._planned_min || 0;
				g.kpi.home_min += s.hometime || 0;
				if (s.dispnumber && !distanceSeen.has(s.dispnumber)) {
					distanceSeen.add(s.dispnumber);
					g.kpi.distance_mi += s._distance_mi || 0;
				}
				if (!s._time_suspicious) {
					const stopTime = (s.esttime || 0) + (s.dispatch_additional || 0);
					if (stopTime > 0) {
						stopMinSum += stopTime;
						stopMinCount++;
					}
				}
				const c = s.datecreated;
				if (c) {
					if (!first || c < first) first = c;
					if (!last || c > last) last = c;
				}
			}
			g.kpi.first = first;
			g.kpi.last = last;
			g.kpi.completion_pct = g.kpi.stops > 0 ? Math.round((g.kpi.completed / g.kpi.stops) * 100) : 0;
			g.kpi.gps_pct = g.kpi.stops > 0 ? Math.round((g.kpi.geocoded / g.kpi.stops) * 100) : 0;
			g.kpi.avg_stop_min = stopMinCount > 0 ? stopMinSum / stopMinCount : 0;
			g.kpi.avg_value = g.kpi.stops > 0 ? g.kpi.value / g.kpi.stops : 0;
			if (first && last) {
				const fm = new Date(first).getTime();
				const lm = new Date(last).getTime();
				g.kpi.span_min = Math.max(0, Math.round((lm - fm) / 60000));
			}
			out.push(g);
		});

		out.sort((a, b) => {
			if (mode === 'dispatch') {
				const an = parseInt(a.key.replace('D-', ''), 10) || 0;
				const bn = parseInt(b.key.replace('D-', ''), 10) || 0;
				return bn - an;
			}
			return b.kpi.stops - a.kpi.stops || this.compareStrings(a.label, b.label);
		});

		this.timelineGroups = out;
	}

	trackGroup = (_: number, g: TimelineGroup) => g.key;

	primaryStreet(r: DispatchReportRow): string {
		const s = (r.ship_line1 || '').trim();
		return s || (r.dispatch_address || '').trim();
	}

	fullAddress(r: DispatchReportRow): string {
		const parts = [
			this.primaryStreet(r),
			r.ship_line2,
			[r.ship_city, r.ship_state].filter(Boolean).join(', '),
			r.ship_postal
		].map((s) => (s || '').trim()).filter(Boolean);
		return parts.join(', ');
	}

	mapsUrl(r: DispatchReportRow): string | null {
		const street = this.primaryStreet(r);
		const parts = [street, r.ship_city, r.ship_state, r.ship_postal]
			.map((x) => (x || '').trim()).filter(Boolean);
		if (!parts.length) return null;
		return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(parts.join(', '));
	}

	isMeaningfulDate(s: string | null | undefined): boolean {
		if (!s) return false;
		return s.indexOf('0000-00-00') === -1;
	}

	exportCsv(): void {
		const rows = this.filteredData;
		if (!rows.length) return;
		const headers = [
			'Disp #', 'Seq', 'Invoice', 'PO', 'Customer', 'Branch', 'Ship to',
			'Address', 'City', 'State', 'ZIP',
			'Phone', 'Email', 'Truck', 'Driver',
			'Route', 'Status', 'Items', 'Qty', 'Value',
			'Est min', 'Add min', 'Home min', 'Distance mi',
			'OOB', 'OC later', 'Cleared', 'Invoiced', 'Passed', 'Lat', 'Lng',
			'Created', 'Updated', 'Delivery'
		];
		const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
		const out = [headers.join(',')];
		for (const r of rows) {
			out.push([
				r.dispnumber, r.fullsort || r.sort,
				r.invoice, r.po_number, r.customerno, r.branchcode, r.ship_to_name,
				this.primaryStreet(r), r.ship_city, r.ship_state, r.ship_postal,
				r.contact_phone || r.so_phone, r.contact_email,
				r.truck_name, r.truck_driver,
				r.route_code, r.status_name,
				r.line_count, r.qty_ordered, r.ext_value,
				r.esttime, r.dispatch_additional, r.hometime, r._distance_mi,
				r.outofbounds, r.oclater, r.cleared, r.invoiced, r.passed,
				r.dispatch_lat ?? '', r.dispatch_lng ?? '',
				r.datecreated, r.dateupdated, this.isMeaningfulDate(r.delivery_date) ? r.delivery_date : ''
			].map(esc).join(','));
		}
		const blob = new Blob([out.join('\r\n')], { type: 'text/csv;charset=utf-8' });
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = `dispatch-report-${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(a.href);
	}

	exportXlsx(): void {
		const rows = this.filteredData.map((r) => ({
			'Disp #': r.dispnumber,
			Seq: r.fullsort || r.sort,
			Invoice: r.invoice,
			PO: r.po_number,
			Customer: r.customerno,
			Branch: r.branchcode,
			'Ship To': r.ship_to_name,
			Address: this.primaryStreet(r),
			City: r.ship_city,
			State: r.ship_state,
			ZIP: r.ship_postal,
			Phone: r.contact_phone || r.so_phone,
			Email: r.contact_email,
			Truck: r.truck_name,
			Driver: r.truck_driver,
			Route: r.route_code,
			Status: r.status_name,
			Items: r.line_count,
			Qty: r.qty_ordered,
			Value: r.ext_value,
			'Est min': r.esttime,
			'Add min': r.dispatch_additional,
			'Home min': r.hometime,
			'Distance mi': r._distance_mi,
			OOB: r.outofbounds,
			'OC later': r.oclater,
			Cleared: r.cleared,
			Invoiced: r.invoiced,
			Passed: r.passed,
			Lat: r.dispatch_lat,
			Lng: r.dispatch_lng,
			Created: r.datecreated,
			Updated: r.dateupdated,
			Delivery: this.isMeaningfulDate(r.delivery_date) ? r.delivery_date : ''
		}));
		this.globalSearchService.exportJsonToExcel(rows, `dispatch-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
	}

	print(): void { window.print(); }
}
